import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { NewProductFields } from 'quote-detail/new-product-fields';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { defaultProduct, discountRecommendationBrands } from 'shared/constants';
import { useQuoteState } from 'shared/contexts';
import { useExecuteModel } from 'shared/mutations';
import { Brands, MarginParamsChild, ResponseResult, ProductForm } from 'shared/types';
import { buildChildModelPayload, buildQuoteProduct } from 'shared/utility';

interface NewProductProps {
  customerId: string;
}

export function NewProduct({ customerId }: NewProductProps) {
  const { brand } = useParams<{ brand: Brands }>();
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const { getValues } = useFormContext();
  const { newProductDialogOpen, toggleNewProductDialog, setTempProduct } = useQuoteState();
  const executeMutation = useExecuteModel<MarginParamsChild, ResponseResult>('marginTarget');

  const methods = useForm<ProductForm>({
    defaultValues: defaultProduct(),
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(defaultProduct());
  }, [newProductDialogOpen, reset]);
  const onClose = useCallback(() => {
    toggleNewProductDialog();
  }, [toggleNewProductDialog]);
  const onSubmit = useCallback(
    async (data: ProductForm) => {
      try {
        const {
          customer: {
            customerNumber,
            customerTier,
            customerType,
            competitionAdjustment,
            competitiveAdjustment,
            customerNumberAdjusted,
          },
          marginFloor,
        } = getValues();

        const modelPayload = buildChildModelPayload(
          {
            customerNumber,
            customerTier,
            customerType,
            competitionAdjustment,
            competitiveAdjustment,
            customerNumberAdjusted,
          },
          data,
          brand,
          marginFloor
        );

        const calcResponse = await executeMutation.mutateAsync(modelPayload);

        const newProduct = buildQuoteProduct(data, calcResponse.data, isDiscountBasedBrand, brand);
        newProduct.calculationId = calcResponse.metadata.calculationId;
        setTempProduct(newProduct);
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
    [getValues, brand, executeMutation, setTempProduct, onClose, isDiscountBasedBrand]
  );

  const buttonText = executeMutation.isLoading ? 'Calculating Prices...' : 'Add Product';
  return (
    <Dialog open={newProductDialogOpen} onClose={onClose}>
      <DialogTitle>Add Product</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form aria-label='form' id='new-product-form' onSubmit={handleSubmit(onSubmit)}>
            <NewProductFields customerId={customerId} />
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type='submit' form='new-product-form' color='secondary' variant='contained'>
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
